import React from 'react';
import { Input } from '@flixbus/honeycomb-react';
import { TextWithEllipsis } from 'app/components/ui/TextWithEllipsis/TextWithEllipsis';
import { Icon, IconPinSolid } from 'app/icons';
import * as styles from './TripLocation.scss';
import { useTranslations } from 'app/hooks/useTranslations';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { useTripLocation } from '../hooks/useTripLocation';
import cn from 'classnames';
import { KEY_NAMES } from 'app/providers/keyNames';
import { emitFieldOpenedEvent } from 'app/events';
import { FIELD_ARRIVAL_CITY, FIELD_DEPARTURE_CITY } from 'app/storage/fieldNames';
import { toggleOverlay } from 'app/store/reducers/ui';
import { useAppDispatch } from 'app/hooks/appHooks';

export const TripLocation = ({ direction }: { direction: 'from' | 'to' }) => {
  const { value } = useTripLocation(direction);
  const dispatch = useAppDispatch();

  const {
    translationGetLabel,
    getFilledDepartureAriaLabel,
    getDepartureAriaLabel,
    getReturnAriaLabel,
    getFilledReturnAriaLabel,
  } = useTranslations({
    translationGetLabel: direction,
    getDepartureAriaLabel: a11yLabelsKeys.locationInputs.departure,
    getFilledDepartureAriaLabel: a11yLabelsKeys.locationInputs.departureFilled,
    getReturnAriaLabel: a11yLabelsKeys.locationInputs.arrival,
    getFilledReturnAriaLabel: a11yLabelsKeys.locationInputs.arrivalFilled,
  });

  const inputAriaLabel =
    direction === 'from'
      ? value
        ? `${getFilledDepartureAriaLabel()} ${value}`
        : getDepartureAriaLabel()
      : value
        ? `${getFilledReturnAriaLabel()} ${value}`
        : getReturnAriaLabel();

  const inputClickHandler = () => {
    emitFieldOpenedEvent(direction === 'from' ? FIELD_DEPARTURE_CITY : FIELD_ARRIVAL_CITY);
    dispatch(
      toggleOverlay({
        name: `TripLocationPanel-${direction}`,
        inputAriaLabel,
        direction,
      }),
    );
  };

  return (
    <>
      <Input
        // @ts-expect-error ignore
        type="button"
        role="button"
        readOnly={true}
        id={`searchInputMobile-${direction}`}
        label={<TextWithEllipsis text={translationGetLabel()} />}
        onClick={inputClickHandler}
        iconLeft={<Icon aria-hidden appearance="primary" InlineIcon={IconPinSolid} />}
        extraClasses={cn(styles.mobileInput, { [styles.arrivalInput]: direction === 'to' })}
        value={value}
        aria-label={inputAriaLabel}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === KEY_NAMES.SPACE || e.key === KEY_NAMES.ENTER) {
            inputClickHandler();
          }
        }}
      />
      <Autocomplete
        direction={direction}
        icon={IconPinSolid}
        label={translationGetLabel() || '...'}
        extraClasses={cn(styles.desktopAutocomplete, {
          [styles.arrivalAutocomplete]: direction === 'to',
        })}
      />
    </>
  );
};

export default TripLocation;
